<script setup lang="ts">
  import {
    ref,
    reactive,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    computed,
    h,
    provide
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, Drawer, Modal, message, Spin, Input } from 'ant-design-vue'
  import {
    PlusOutlined,
    CodeOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
  } from '@ant-design/icons-vue'
  import { CustomReport } from './models/CustomReport'
  import { CustomReportSchema } from './models/CustomReportSchema'
  import { BookText, Download, Loader2, Trash2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { debounceTime, Subject } from 'rxjs'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { CustomReportSchemaUiFieldGroup } from './models/CustomReportSchemaUi'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { useUser } from '@/composables/useUser'
  import { toast } from 'vue-sonner'
  import ExecutionSelectionTable from '../ExecutionHistory/components/ExecutionSelectionTable.vue'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import { AnalysisWizardStepType } from '../AnalysisWizard/models/AnyalsisWizardStepType'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import {
    CustomReportsApiService,
    ICustomReportsApiService
  } from './services/CustomReportApiService'
  import { GetExecutionPostProcessingOutputArgs } from '@/shared/Executions/models/GetExecutionPostProcessingOutputArgs'
  import { PostProcessingOutputType } from '@/models/PostProcessingOutputType'
  import { CustomReportsService } from './services/CustomReportsService'
  import { CustomReportsJsonParsingService } from './services/CustomReportsJsonParsingService'
  import { CustomReportsSearchService } from './services/CustomReportsSearchService'
  import { CustomReportsUiService } from './services/CustomReportsUiService'
  import CustomReportConfiguration from './components/CustomReportConfiguration.vue'
  import { CustomReportsTypeGuardService } from './services/CustomReportsTypeGuardService'
  import { CustomReportJsonReportConfigurationService } from './services/CustomReportJsonReportConfigurationService'
  import JsonCustomReportConfiguration from './components/JsonCustomReportConfiguration.vue'
  import CustomReportSelectionModal from './components/CustomReportSelectionModal.vue'
  import CustomReportJsonManagement from './components/CustomReportJsonManagement.vue'
  import { PostProcessingOutput } from '@/models/PostProcessingOutput'
  import { PostProcessingOutputPlot } from '@/models/PostProcessingOutputPlot'
  import { PostProcessingOutputTable } from '@/models/PostProcessingOutputTable'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import {
    ExecutionPhaseService,
    IExecutionPhaseService
  } from '@/shared/Executions/services/ExecutionPhaseService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'
  import { useSortedOutputs } from './services/useSortedCustomOutputs'
  import TemplateSelector from '@/components/TemplateManagement/TemplateSelector.vue'
  import type { TemplateSelection } from '@/components/TemplateManagement/models'

  interface ICustomReportsProps {
    customReports?: Array<CustomReport>
    customReportsExecution?: Execution
    postProcessingExecution?: Execution
    customPlotsOutput?: PostProcessingOutput<PostProcessingOutputPlot>
    customTablesOutput?: PostProcessingOutput<PostProcessingOutputTable>
    mode?: 'multiconfig' | 'singleconfig'
    wizardModel?: AnalysisWizardModel
    postProcessingSelectionDisabled?: boolean // in the wizard this component should rely on an input postProcessingExecution
  }

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  const customReportsApiService: ICustomReportsApiService = InjectionService.injectStrict(
    CustomReportsApiService.injectableKey
  )

  const customReportsJsonParsingService: CustomReportsJsonParsingService =
    InjectionService.injectStrict(CustomReportsJsonParsingService.injectableKey)

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const executionPhaseService: IExecutionPhaseService = InjectionService.injectStrict(
    ExecutionPhaseService.injectableKey
  )

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<ICustomReportsProps>()

  const emit = defineEmits<{
    (e: 'update:customReports', value: Array<CustomReport>): void
    (e: 'update:customReportsExecution', value?: Execution): void
    (e: 'update:customPlotsOutput', value?: PostProcessingOutput<PostProcessingOutputPlot>): void
    (e: 'update:customTablesOutput', value?: PostProcessingOutput<PostProcessingOutputTable>): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const executionName = ref<string>(props.customReportsExecution?.name ?? '')
  const execution = ref<Execution | undefined>(props.customReportsExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  const isExecuted = computed(() => execution.value?.id != null)

  watch(
    () => props.customReportsExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  watch(
    () => executionName.value,
    (val) => {
      // invalidate existing execution if previously executed and use new instance to store name
      if (execution.value?.id || !execution.value) {
        execution.value = { name: val } as Execution
      } else if (execution.value) {
        execution.value.name = val
      }
      emit('update:customReportsExecution', execution.value)
    },
    { immediate: false }
  )

  const auth = useUser()

  // Prevent uneccessary remount of component E.g on sharedWizardModel emits
  const suppressNextPropsCustomReportsUpdate = ref(false)

  const isDownloadingResults = ref<boolean>(false)
  const isLoadingCustomOutputs = ref<boolean>(false)
  const selectedTemplateSelection = ref<TemplateSelection | null>(null)
  const selectedCustomPlotsOutput = ref<PostProcessingOutput<PostProcessingOutputPlot> | undefined>(
    props.customPlotsOutput
  )
  const selectedCustomTablesOutput = ref<
    PostProcessingOutput<PostProcessingOutputTable> | undefined
  >(props.customTablesOutput)
  const isLoadingSchemas = ref<boolean>(false)
  const hasErrorLoadingSchemas = ref<boolean>(false)
  const availableCustomReportSchemas = ref<CustomReportSchema[]>([])
  const activeCustomReports = reactive<CustomReport[]>([])
  const selectedCustomReport = ref<CustomReport | null>(null)
  const showCustomReportSelection = ref(false)
  const showJsonManagement = ref(false)
  const showPostProcessingSelectionModal = ref(false)
  const selectedPostProcessingExecutionId = ref<string>()
  const activeLeftPanelTabKey = ref<string>()

  const sortedSelectedCustomPlotsOutputs = useSortedOutputs(selectedCustomPlotsOutput)
  const sortedSelectedCustomTablesOutputs = useSortedOutputs(selectedCustomTablesOutput)

  const setCustomOutputsForPostProcessingExecution = async (
    postProcessingExecution: Execution | string
  ): Promise<{
    customPlots: PostProcessingOutput<PostProcessingOutputPlot>
    customTables: PostProcessingOutput<PostProcessingOutputTable>
  }> => {
    const postProcessingExecutionId =
      typeof postProcessingExecution === 'string'
        ? postProcessingExecution
        : postProcessingExecution?.id

    if (!postProcessingExecutionId) {
      toast.error('A valid Post Processing Execution is required to load Custom Plots for Reports.')
    }

    try {
      isLoadingCustomOutputs.value = true
      await auth.initialize()

      const customPlotsArgs = new GetExecutionPostProcessingOutputArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: postProcessingExecutionId!,
        outputType: PostProcessingOutputType.CustomPlots
      })

      const customTablesArgs = new GetExecutionPostProcessingOutputArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: postProcessingExecutionId!,
        outputType: PostProcessingOutputType.CustomTables
      })

      // run both calls in parallel while runAsyncTaskWithLoading controls the loading state
      const [plotsResp, tablesResp] =
        (await AnalysisWizardService.runAsyncTaskWithLoading(
          'Loading Inputs',
          props.wizardModel,
          async () => {
            return Promise.all([
              executionApiService.getPostProcessingExecutionOutput<PostProcessingOutputPlot>(
                customPlotsArgs
              ),
              executionApiService.getPostProcessingExecutionOutput<PostProcessingOutputTable>(
                customTablesArgs
              )
            ])
          }
        )) ?? []

      selectedCustomPlotsOutput.value =
        plotsResp?.data ?? new PostProcessingOutput<PostProcessingOutputPlot>()
      selectedCustomTablesOutput.value =
        tablesResp?.data ?? new PostProcessingOutput<PostProcessingOutputTable>()

      return {
        customPlots: selectedCustomPlotsOutput.value,
        customTables: selectedCustomTablesOutput.value
      }
    } catch (error) {
      toast.error('An error was encountered loading plots and/or tables for reports.')
      throw error
    } finally {
      isLoadingCustomOutputs.value = false
    }
  }

  const isProcessingReports = ref(false)
  const draggedCustomReport = ref<CustomReport | null>(null)
  const draggedPostProcessingItem = ref<any | null>(null)
  const highlightedCustomReportId = ref<string | null>(null)
  const deletingCustomReportId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeCustomReportItemTabKey = ref<string>('view')
  const activeCustomReportItemViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeCustomReportEditConfiguration = ref<{} | null>(null)

  watch(
    selectedCustomReport,
    (newVal) => {
      if (newVal) {
        const flattenedCustomReport = CustomReportsService.getFlattenedCustomReports([newVal])
        activeCustomReportEditConfiguration.value = flattenedCustomReport?.length
          ? flattenedCustomReport[0]
          : null
      }
    },
    { deep: false }
  )

  const onCustomReportViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedCustomReport.value == null || activeCustomReportEditConfiguration.value == null)
      return
    if (activeMode === 'edit') {
      const flattendCustomReport = CustomReportsService.getFlattenedCustomReports([
        selectedCustomReport.value
      ])
      activeCustomReportEditConfiguration.value = flattendCustomReport?.length
        ? flattendCustomReport[0]
        : null
    }
  }

  const loadAvailableCustomReportsSchemas = async () => {
    hasErrorLoadingSchemas.value = false
    isLoadingSchemas.value = true
    try {
      const customReportSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.CustomReportSchema,
        customSchemaStore
      )

      availableCustomReportSchemas.value = customReportSchemas
    } catch (error: any) {
      hasErrorLoadingSchemas.value = true
      message.error('Failed to load available custom reports.')
    } finally {
      isLoadingSchemas.value = false
    }
  }

  const addCustomReport = (customReportSchema: CustomReportSchema): CustomReport => {
    const newCustomReport =
      CustomReportsService.createCustomReportFromCustomReportSchema(customReportSchema)
    activeCustomReports.push(newCustomReport)
    selectedCustomReport.value = newCustomReport
    updateSharedModelOnReportsChange()
    message.success(`Added report: ${customReportSchema.display_name}`)
    return newCustomReport
  }

  const selectCustomReport = (customReport: CustomReport) => {
    selectedCustomReport.value = customReport
  }

  const removeCustomReportTimeoutMs: number = 600
  const removeCustomReport = (customReport: CustomReport) => {
    animateCustomReportDeletion(customReport.id as string)
    message.success(`Removed "${customReport.display_name}"`)
  }

  const animateCustomReportDeletion = (customReportId: string) => {
    deletingCustomReportId.value = customReportId
    setTimeout(() => {
      const idx = activeCustomReports.findIndex((c) => c.id === customReportId)
      if (idx > -1) activeCustomReports.splice(idx, 1)
      if (selectedCustomReport.value?.id === customReportId) {
        if (activeCustomReports?.length > 0) {
          selectedCustomReport.value = activeCustomReports[0]
        } else {
          selectedCustomReport.value = null
        }
      }
      deletingCustomReportId.value = null
      updateSharedModelOnReportsChange()
    }, removeCustomReportTimeoutMs)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Handle file selection from Excution History
  const handlePostProcessingExecutionSelected = async (execution: Execution) => {
    showPostProcessingSelectionModal.value = false
    const primaryExecutionPhase =
      executionPhaseService.tryGetPrimaryPhaseTypeForExecution(execution)
    if (primaryExecutionPhase !== ExecutionPhaseType.Visualization) {
      toast.error('Only post processing executions may be used in configuring custom reports')
      //return
    }

    selectedPostProcessingExecutionId.value = execution?.id
    if (!selectedCustomPlotsOutput.value) {
      const outputs: {
        customPlots: PostProcessingOutput<PostProcessingOutputPlot>
        customTables: PostProcessingOutput<PostProcessingOutputTable>
      } = await setCustomOutputsForPostProcessingExecution(execution)
      emit('update:customPlotsOutput', outputs.customPlots)
      emit('update:customTablesOutput', outputs.customTables)
    }
    return
  }

  const clearPostProcessingExecutionSelection = () => {
    Modal.confirm({
      title: 'Confirm',
      content: 'Clear existing custom reports?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        selectedPostProcessingExecutionId.value = undefined
        selectedCustomPlotsOutput.value = undefined
        selectedCustomTablesOutput.value = undefined

        activeCustomReports.splice(0, activeCustomReports.length)
        selectedCustomReport.value = null

        if (!props.wizardModel) {
          activeLeftPanelTabKey.value = 'reports'
        }
      },
      onCancel() {}
    })
  }

  // Initialize flatRules or load for cleanedFileId and
  const initializeFlatCustomReports = (flatCustomReports?: Array<CustomReport>) => {
    try {
      isLoadingSchemas.value = true
      const initializedCustomReports: Array<CustomReport> = []
      let inputCustomReports = flatCustomReports || []
      inputCustomReports.forEach((flatCustomReport: CustomReport) => {
        const initializedCustomReport = customReportsJsonParsingService.initializeEntity(
          flatCustomReport,
          availableCustomReportSchemas.value,
          1
        )
        initializedCustomReports.push(initializedCustomReport)
      })
      // Replace the contents of the reactive array so Vue picks up the change
      activeCustomReports.splice(0, activeCustomReports.length, ...initializedCustomReports)
      // Set the first rule as active
      if (activeCustomReports.length > 0) selectedCustomReport.value = activeCustomReports[0]
    } catch {
      message.error('Failed to initialize custom reports.')
    } finally {
      isLoadingSchemas.value = false
    }
  }

  watch(
    () => props.postProcessingExecution,
    async (postProcessingExecution?: Execution) => {
      if (postProcessingExecution) {
        handlePostProcessingExecutionSelected(postProcessingExecution)
      }
    },
    { immediate: true }
  )

  const onSubmitCustomReportsForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content:
          'Re-executing Custom Reports will reset any prior Custom Report output here. You can still access prior executions via Analysis History.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitCustomReportsForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitCustomReportsForGeneration()
    }
  }

  const submitCustomReportsForGeneration = async () => {
    isProcessingReports.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const flattenedCustomReports =
        CustomReportsService.getFlattenedCustomReports(activeCustomReports)

      const args = CustomReportsService.createQueueCustomReportsArgs(
        selectedPostProcessingExecutionId.value as string,
        flattenedCustomReports,
        executionName.value,
        selectedTemplateSelection.value
      )

      emit('update:customReports', args.customReports)

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Generating Report',
        props.wizardModel,
        customReportsApiService.processCustomReports.bind(customReportsApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processCustomReportsResponse = response?.data as QueueExecutionResponse
      if (!processCustomReportsResponse?.executionId)
        throw Error('An unexpected error has occurred!')
      const successMessage = 'Generating Report'
      /* processRulesResponse?.message?.length
          //? processRulesResponse.message
          : 'Data cleaning queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processCustomReportsResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:customReportsExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingReports.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered generating report:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:customReportsExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:customReportsExecution', ex)
    isProcessingReports.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered generating report')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Generating Report')
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingReports.value ||
      execution.value?.id == null ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const moveCustomReportUp = (customReport: CustomReport) => {
    const currentIndex = activeCustomReports.findIndex((c) => c.id === customReport.id)
    if (currentIndex > 0) {
      const temp = activeCustomReports[currentIndex]
      activeCustomReports[currentIndex] = activeCustomReports[currentIndex - 1]
      activeCustomReports[currentIndex - 1] = temp
      highlightMovedCustomReport(customReport.id as string)
      message.success(`Moved "${customReport.display_name}" up to position ${currentIndex}`)
    }
  }

  const moveCustomReportDown = (customReport: CustomReport) => {
    const currentIndex = activeCustomReports.findIndex((c) => c.id === customReport.id)
    if (currentIndex < activeCustomReports.length - 1) {
      const temp = activeCustomReports[currentIndex]
      activeCustomReports[currentIndex] = activeCustomReports[currentIndex + 1]
      activeCustomReports[currentIndex + 1] = temp
      highlightMovedCustomReport(customReport.id as string)
      message.success(`Moved "${customReport.display_name}" down to position ${currentIndex + 2}`)
    }
  }

  const highlightMovedCustomReport = (customReportId: string) => {
    highlightedCustomReportId.value = customReportId
    setTimeout(() => {
      highlightedCustomReportId.value = null
    }, 1500)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, customReport: CustomReport) => {
    draggedCustomReport.value = customReport
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', customReport.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    if (draggedCustomReport.value) {
      const draggedIndex = activeCustomReports.findIndex(
        (c) => c.id === draggedCustomReport.value!.id
      )
      if (draggedIndex !== targetIndex) {
        const [movedCustomReport] = activeCustomReports.splice(draggedIndex, 1)
        activeCustomReports.splice(targetIndex, 0, movedCustomReport)
        highlightMovedCustomReport(draggedCustomReport.value.id as string)
        message.success(
          `Moved "${draggedCustomReport.value.display_name}" to position ${targetIndex + 1}`
        )
      }
    }
    draggedCustomReport.value = null
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    draggedCustomReport.value = null
    dragOverIndex.value = -1
  }

  const handlePostProcessingDragStart = (event: DragEvent, item: any, source?: string) => {
    draggedPostProcessingItem.value = item
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy'
      try {
        const payloadJson = JSON.stringify({ source: source ?? 'postprocessing', item })
        event.dataTransfer.setData('application/json', payloadJson)
        ;(window as any).__postProcessingDragPayloadJson = payloadJson
      } catch {
        // ignore serialization errors
      }
      try {
        // also set a plain text representation
        event.dataTransfer.setData('text/plain', (window as any).__postProcessingDragPayloadJson)
      } catch {}
    }
  }

  const handlePostProcessingDragEnd = () => {
    draggedPostProcessingItem.value = null
    ;(window as any).__postProcessingDragPayloadJson = undefined
  }

  const handleJsonEditorConfigurationChange = () => {
    if (selectedCustomReport.value != null && activeCustomReportEditConfiguration.value != null) {
      const editConfigurationJson = JSON.stringify([activeCustomReportEditConfiguration.value])
      const result = customReportsJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availableCustomReportSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingCustomReportIndex = activeCustomReports.findIndex(
          (x) => x.id === selectedCustomReport.value?.id
        )
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingCustomReportIndex <= -1
        )
          throw new Error('Error updating report from Json Report Editor value!')
        const updatedCustomReport = (result.parsedEntities as Array<CustomReport>)[0]
        const existingCustomReport = activeCustomReports[existingCustomReportIndex]
        Object.assign(existingCustomReport, { ...updatedCustomReport, id: existingCustomReport.id })
        selectedCustomReport.value = existingCustomReport
      }
    }
  }

  const shouldClearCustomReportsExecution = computed(() => isExecuted.value && !isExecuting.value)

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnReportsChange = () => {
    const customReportsExecution = shouldClearCustomReportsExecution.value
      ? undefined
      : execution.value
    emit('update:customReportsExecution', customReportsExecution)
    const flattenedCustomReports =
      CustomReportsService.getFlattenedCustomReports(activeCustomReports)
    suppressNextPropsCustomReportsUpdate.value = true
    emit('update:customReports', flattenedCustomReports)
  }

  const handleConfigurationUpdate = async (
    customReport: CustomReport,
    config: Record<string, any>,
    customReportTypeChange?: boolean
  ) => {
    const customReportSearchResults = CustomReportsSearchService.findCustomReportByIdRecursive(
      activeCustomReports,
      customReport.id as string
    )
    if (customReportSearchResults != null) {
      const topLevelParentCustomReportIndex = activeCustomReports.findIndex(
        (c) => c.id === customReportSearchResults.topLevelParent?.id
      )
      if (topLevelParentCustomReportIndex > -1) {
        if (customReportTypeChange === true) {
          const newCustomReportSchema = CustomReportsService.tryGetCustomReportSchemaByType(
            customReport._type,
            availableCustomReportSchemas.value
          )
          if (newCustomReportSchema == null)
            throw new Error(`Error getting ReportSchema for report.type: ${customReport.type}!`)
          selectedCustomReport.value = null
          let newCustomReport =
            CustomReportsService.createCustomReportFromCustomReportSchema(newCustomReportSchema)
          newCustomReport.description = customReport.configuration?.description
          newCustomReport.configuration = { ...config }
          if (
            customReportSearchResults.topLevelParent != null &&
            customReportSearchResults.topLevelParent !== customReportSearchResults.customReport &&
            customReportSearchResults.path?.length > 1
          ) {
            throw new Error('Custom Report containing child report is not supported.')
          } else {
            activeCustomReports.splice(topLevelParentCustomReportIndex, 1, newCustomReport)
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedCustomReport.value = activeCustomReports[topLevelParentCustomReportIndex]
          })
        } else if (
          customReportSearchResults.topLevelParent === customReportSearchResults.customReport
        ) {
          activeCustomReports[topLevelParentCustomReportIndex].configuration = { ...config }
          activeCustomReports[topLevelParentCustomReportIndex]._type = config._type
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnReportsChange()
      }
    }
  }

  const onJsonManagementCustomReportsApplied = () => {
    selectedCustomReport.value = activeCustomReports?.length ? activeCustomReports[0] : null
    updateSharedModelOnReportsChange()
  }

  const uiFieldGroups = computed(() => {
    return CustomReportsUiService.getCustomReportSchemaUiFieldGroups(
      selectedCustomReport.value?.schema
    )
  })

  // Ensure first tab is selected on update of selected custom report
  // persist JSON tab selection if already active
  watch(
    [selectedCustomReport, uiFieldGroups],
    ([customReport, groups]) => {
      if (customReport != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activeCustomReportItemTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (
          !groups.some(
            (g: CustomReportSchemaUiFieldGroup) => g.name === activeCustomReportItemTabKey.value
          )
        ) {
          activeCustomReportItemTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  //@ts-ignore
  const goToWizardStepByType = (stepType: AnalysisWizardStepType) => {
    if (props.wizardModel != null) {
      wizardNavigationService.setActiveWizardStepByType(stepType, props.wizardModel)
    }
  }

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (
      selectedCustomReport.value == null ||
      selectedCustomReport.value._type === CustomReportsService.CUSTOM_CUSTOMREPORT_TYPE
    )
      return true
    const activeSchemaType = CustomReportsService.tryGetCustomReportSchemaByType(
      selectedCustomReport.value._type,
      availableCustomReportSchemas.value
    )
    return (
      activeSchemaType == null ||
      activeSchemaType.type === CustomReportsService.CUSTOM_CUSTOMREPORT_TYPE
    )
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeCustomReportItemViewMode.value = { mode: 'edit' }
      } else if (activeCustomReportItemViewMode.value.mode !== 'edit') {
        activeCustomReportItemViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.customCalculationsExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.customCalculationsExecution?.id == null
      ) {
        stepStatusMessage = 'Custom Calculations not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailableCustomReportsSchemas()

    // Apply the watch inside the onMounted so it runs after we have loaded schemas
    // When in singleconfig mode, require the input anaylsisConfig prop and populate activeAnalysisConfigs
    watch(
      () => [props.mode, props.customReports],
      ([mode, inputCustomReports]) => {
        if (suppressNextPropsCustomReportsUpdate.value) {
          suppressNextPropsCustomReportsUpdate.value = false
          return
        }

        if (mode === 'singleconfig') {
          // single mode requires an analysis config prop
          if (!inputCustomReports?.length || !props.customReports?.length) {
            if (!availableCustomReportSchemas.value?.length) {
              message.error('Missing Custom Report Schemas!', defaultMessageDurationSeconds)
              throw Error('Missing custom report schemas!')
            }

            const firstSchema = availableCustomReportSchemas.value[0]
            const newCustomReport =
              CustomReportsService.createCustomReportFromCustomReportSchema(firstSchema)
            newCustomReport.configuration =
              CustomReportsService.initializeCustomReportConfiguration(
                newCustomReport,
                firstSchema.fields
              )
            // clear any previous state that is only relevant for multiconfig
            activeCustomReports.splice(0, activeCustomReports.length)
            selectedCustomReport.value = newCustomReport
            const flattenedCustomReports = CustomReportsService.getFlattenedCustomReports([
              newCustomReport
            ])
            emit('update:customReports', flattenedCustomReports)
          } else if (props.customReports?.length) {
            const firstCustomReport = props.customReports[0]
            // TODO: confirm we can store type data with executed configurations
            if (firstCustomReport.type == null) {
              const firstSchemaType = availableCustomReportSchemas.value?.length
                ? availableCustomReportSchemas.value[0]
                : null

              if (firstSchemaType == null) {
                message.error('Missing required Report Config CDN Schema content!')
              } else {
                firstCustomReport.type = firstSchemaType.type
                emit('update:customReports', [firstCustomReport])
              }
            }

            // Initialize configuration from schema so we can build UI
            const initializedCustomReport = customReportsJsonParsingService.initializeEntity(
              firstCustomReport,
              availableCustomReportSchemas.value,
              1
            )
            activeCustomReports.splice(0, activeCustomReports.length, initializedCustomReport)

            if (activeCustomReports.length > 0) {
              selectedCustomReport.value = activeCustomReports[0]
            }
          }
        }
      },
      { immediate: true }
    )

    updateWizardStepStatus()

    // Watch input flat rules and ensure initialized (after schemas loaded)
    initializeFlatCustomReports(props.customReports)

    watch(
      activeCustomReports,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // --- Add this watcher to reset tab selection on selectedCalculation change ---
  watch(selectedCustomReport, (newCustomReport, oldCustomReport) => {
    if (props.mode !== 'singleconfig' && newCustomReport && newCustomReport !== oldCustomReport) {
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeCustomReportItemTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        activeCustomReportItemViewMode.value = { mode: 'view' }
      } else {
        activeCustomReportItemViewMode.value = { mode: 'edit' }
      }
    }
  })

  const { isExtraLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeCustomReports: activeCustomReports,
    addCustomReport: addCustomReport,
    removeCustomReport: removeCustomReport
  })
</script>

<template>
  <div
    :class="[
      'reports-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'h-full': isFitToBrowser,
        'reports-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter class="h-full">
      <SplitterPanel :size="30" style="min-width: 350px">
        <div :class="['flex', 'flex-col', 'border-r-2', 'shadow-sm', 'h-full']">
          <!-- Analysis Selection UI -->
          <div class="p-4 pb-0 shadow-sm">
            <div class="mb-3" v-if="!postProcessingSelectionDisabled">
              <label class="mb-1 block text-sm font-medium">Source Execution</label>
              <div class="mb-2 flex items-center gap-2">
                <!-- Select Analysis -->
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  :disabled="isLoadingCustomOutputs || isLoadingSchemas"
                  block
                  @click="showPostProcessingSelectionModal = true"
                  :title="
                    selectedPostProcessingExecutionId
                      ? 'Change selected Execution'
                      : 'Select an Execution'
                  "
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedPostProcessingExecutionId ?? 'Select Execution' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="selectedPostProcessingExecutionId && !isLoadingCustomOutputs"
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearPostProcessingExecutionSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Name</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Execution Name' }}
                </template>
                <Input
                  v-model:value="executionName"
                  size="large"
                  :disabled="
                    (!selectedPostProcessingExecutionId && activeCustomReports.length === 0) ||
                    isProcessingReports
                  "
                  placeholder="Execution Name"
                />
              </a-tooltip>
            </div>
            <div class="mb-3">
              <TemplateSelector
                v-model="selectedTemplateSelection"
                label="Report Template"
              />
            </div>
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Custom Report</h3>
                <p class="mt-1 text-sm">
                  {{ activeCustomReports.length }}
                  {{ activeCustomReports.length === 1 ? 'report' : 'reports' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip v-if="!wizardModel">
                  <template #title>
                    {{ 'Add New Report' }}
                  </template>
                  <Button
                    :disabled="
                      selectedPostProcessingExecutionId == null || selectedCustomPlotsOutput == null
                    "
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showCustomReportSelection = true"
                    title="Add New Report"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagement = true"
                    title="View/Export JSON"
                    :disabled="
                      (!selectedPostProcessingExecutionId && activeCustomReports.length === 0) ||
                      isProcessingReports
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="reports-list-container flex-1 p-0">
            <!-- Reports / Plots / Tables Tabs -->
            <div class="reports-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <a-tabs
                v-model:activeKey="activeLeftPanelTabKey"
                size="small"
                type="line"
                :tabBarStyle="{ paddingLeft: '25px' }"
              >
                <a-tab-pane key="reports" tab="Reports" v-if="!wizardModel">
                  <!-- Existing Reports List (unchanged) -->
                  <div>
                    <!-- Empty State -->
                    <div
                      v-if="activeCustomReports.length === 0"
                      class="flex h-full flex-col items-center justify-start py-12 text-center"
                    >
                      <div class="mb-6">
                        <div
                          class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                        >
                          <svg
                            class="h-10 w-10 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                      </div>
                      <h3 class="mb-2 text-lg font-semibold">No Reports Yet</h3>
                      <!-- Quick Tips -->
                      <div
                        class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                      >
                        <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                          💡 Quick Tips
                        </h4>
                        <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                          <li>
                            • Reports are created and configured after selecting a Post Processing
                            Execution
                          </li>
                          <li>• Use the + button to add a custome report</li>
                          <li>• Configure each report in the right panel</li>
                        </ul>
                      </div>
                    </div>

                    <div v-else class="mt-2 space-y-1.5 px-1 pb-5">
                      <Card
                        v-for="(customReport, index) in activeCustomReports"
                        :key="customReport.id"
                        :class="[
                          'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                          {
                            'ring-2 ring-blue-500': selectedCustomReport?.id === customReport.id,
                            'opacity-50': draggedCustomReport?.id === customReport.id,
                            'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                              dragOverIndex === index,
                            'rule-moved-highlight': highlightedCustomReportId === customReport.id,
                            'rule-deleting': deletingCustomReportId === customReport.id
                          }
                        ]"
                        :bodyStyle="{
                          padding: '18px'
                        }"
                        draggable="true"
                        @click="selectCustomReport(customReport)"
                        @dragstart="handleDragStart($event, customReport)"
                        @dragover="handleDragOver($event, index)"
                        @dragleave="handleDragLeave"
                        @drop="handleDrop($event, index)"
                        @dragend="handleDragEnd"
                      >
                        <div>
                          <div class="flex items-start justify-between">
                            <!-- Drag Handle -->
                            <div class="mr-3 flex items-center">
                              <HolderOutlined
                                class="cursor-grab text-gray-400 active:cursor-grabbing"
                              />
                            </div>
                            <div class="min-w-0 flex-1">
                              <h4 class="truncate text-sm font-medium leading-tight">
                                {{ customReport.display_name }}
                              </h4>
                              <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                                {{ customReport.configuration?.description ?? '&nbsp;' }}
                              </p>
                            </div>
                            <!-- Report Controls -->
                            <div
                              class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                            >
                              <Button
                                type="text"
                                size="small"
                                :disabled="
                                  activeCustomReports.findIndex((c) => c.id === customReport.id) ===
                                  0
                                "
                                class="flex h-5 w-5 items-center justify-center"
                                @click.stop="moveCustomReportUp(customReport)"
                              >
                                <ArrowUpOutlined class="text-xs" />
                              </Button>
                              <Button
                                type="text"
                                size="small"
                                :disabled="
                                  activeCustomReports.findIndex((c) => c.id === customReport.id) ===
                                  activeCustomReports.length - 1
                                "
                                class="flex h-5 w-5 items-center justify-center"
                                @click.stop="moveCustomReportDown(customReport)"
                              >
                                <ArrowDownOutlined class="text-xs" />
                              </Button>
                              <Button
                                type="link"
                                size="small"
                                danger
                                class="flex h-8 w-8 items-center justify-center"
                                @click.stop="removeCustomReport(customReport)"
                              >
                                <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                </a-tab-pane>

                <a-tab-pane
                  key="plots"
                  tab="Plots"
                  :disabled="!sortedSelectedCustomPlotsOutputs?.length"
                >
                  <div
                    v-if="!sortedSelectedCustomPlotsOutputs?.length"
                    class="py-6 text-center text-sm text-gray-500"
                  >
                    No plots available
                  </div>
                  <div v-else class="space-y-1.5 px-1 pb-5">
                    <Card
                      v-for="outputItem in sortedSelectedCustomPlotsOutputs"
                      :key="outputItem.id"
                      :class="[
                        'group cursor-grab bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]'
                      ]"
                      :bodyStyle="{ padding: '18px' }"
                      draggable="true"
                      @dragstart="
                        handlePostProcessingDragStart($event, outputItem, 'postprocessing')
                      "
                      @dragend="handlePostProcessingDragEnd"
                    >
                      <div>
                        <div class="flex items-start justify-between">
                          <div class="mr-3 flex items-center">
                            <HolderOutlined
                              class="cursor-grab text-gray-400 active:cursor-grabbing"
                            />
                          </div>
                          <div class="min-w-0 flex-1">
                            <h4 class="truncate text-sm font-medium leading-tight">
                              {{ outputItem.id ? outputItem.id : 'Plot' }}
                            </h4>
                            <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                              {{ `Source: ${outputItem.sourceFileName}` }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </a-tab-pane>

                <a-tab-pane
                  key="tables"
                  tab="Tables"
                  :disabled="!sortedSelectedCustomTablesOutputs.length"
                >
                  <div
                    v-if="!sortedSelectedCustomTablesOutputs.length"
                    class="py-6 text-center text-sm text-gray-500"
                  >
                    No tables available
                  </div>
                  <div v-else class="space-y-1.5 px-1 pb-5">
                    <Card
                      v-for="outputItem in sortedSelectedCustomTablesOutputs"
                      :key="outputItem.id"
                      :class="[
                        'group cursor-grab bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]'
                      ]"
                      :bodyStyle="{ padding: '18px' }"
                      draggable="true"
                      @dragstart="
                        handlePostProcessingDragStart($event, outputItem, 'postprocessing')
                      "
                      @dragend="handlePostProcessingDragEnd"
                    >
                      <div>
                        <div class="flex items-start justify-between">
                          <div class="mr-3 flex items-center">
                            <HolderOutlined
                              class="cursor-grab text-gray-400 active:cursor-grabbing"
                            />
                          </div>
                          <div class="min-w-0 flex-1">
                            <h4 class="truncate text-sm font-medium leading-tight">
                              {{ outputItem.id ? outputItem.id : 'Table' }}
                            </h4>
                            <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                              {{ `Source: ${outputItem.sourceFileName}` }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>
          </div>
        </div>
      </SplitterPanel>
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedCustomReport
                    ? `Configure: ${selectedCustomReport.display_name}`
                    : 'Reports Configuration'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedPostProcessingExecutionId != null
                    ? selectedCustomReport != null
                      ? 'Configure the selected report parameters below'
                      : 'Select a report from the left panel to configure its settings'
                    : 'Select a post processing execution to begin configuring reports'
                }}
              </p>
            </div>
            <div class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    !execution?.id || !activeCustomReports?.length
                      ? 'Generate Report to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <a-tooltip
                :open="
                  selectedPostProcessingExecutionId == null ||
                  !activeCustomReports?.length ||
                  isProcessingReports
                    ? false
                    : undefined
                "
              >
                <template #title>
                  {{
                    selectedPostProcessingExecutionId == null || !activeCustomReports?.length
                      ? 'Select an execution and add reports to Generate Report'
                      : !isProcessingReports
                        ? 'Generate Report'
                        : 'Generating Report'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedPostProcessingExecutionId == null ||
                    !activeCustomReports?.length ||
                    isProcessingReports
                  "
                  @click="onSubmitCustomReportsForGenerationClicked"
                >
                  <Loader2 v-if="isProcessingReports" class="size-4 animate-spin" />
                  <BookText v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedPostProcessingExecutionId == null ||
                    !activeCustomReports?.length ||
                    isProcessingReports
                  "
                  @click="onSubmitCustomReportsForGenerationClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingReports" class="size-4 animate-spin" />
                    <BookText v-else class="h-4 w-4" />
                    {{ 'Generate Report' }}
                  </span>
                </Button>
              </a-tooltip>

              <!-- View/Export JSON-->
              <a-tooltip placement="left">
                <template #title>
                  {{ 'View/Export JSON' }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedCustomPlotsOutput && activeCustomReports.length === 0) ||
                    isProcessingReports
                  "
                >
                  <CodeOutlined class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  :type="
                    selectedCustomPlotsOutput && activeCustomReports.length !== 0
                      ? 'button'
                      : 'default'
                  "
                  :class="{
                    'small-button': selectedCustomPlotsOutput && activeCustomReports.length !== 0
                  }"
                  @click="showJsonManagement = true"
                  title="View/Export JSON"
                  :disabled="
                    (!selectedCustomPlotsOutput && activeCustomReports.length === 0) ||
                    isProcessingReports
                  "
                >
                  <span class="ant-button-flex-content">
                    <CodeOutlined class="h-4 w-4" />
                    {{ 'View/Export JSON' }}
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>
          <div
            v-if="(isLoadingSchemas || isLoadingCustomOutputs) && !wizardModel"
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="selectedPostProcessingExecutionId == null && !hasErrorLoadingSchemas"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select a post processing execution to begin configuring reports
            </p>
            <div v-else-if="hasErrorLoadingSchemas" class="mt-8 flex justify-center text-red-400">
              <span class="anticon anticon-warning-filled mr-2 text-2xl" />
              An error was encountered loading reports.
            </div>

            <div
              v-else-if="
                selectedCustomReport != null && uiFieldGroups != null && uiFieldGroups?.length
              "
              class="min-h-0 flex-1"
            >
              <a-tabs
                :key="selectedCustomReport?.id"
                v-model:activeKey="activeCustomReportItemTabKey"
                @change="onCustomReportViewModeTabChange"
                :destroyInactiveTabPane="true"
              >
                <a-tab-pane
                  v-for="(fieldGroup, index) in uiFieldGroups"
                  :key="fieldGroup.name"
                  :tab="fieldGroup.name"
                >
                  <CustomReportConfiguration
                    :configuration-level="1"
                    :custom-report="selectedCustomReport"
                    :custom-plots-output="selectedCustomPlotsOutput"
                    :custom-tables-output="selectedCustomTablesOutput"
                    :ui-field-group="fieldGroup"
                    @configuration-updated="handleConfigurationUpdate"
                    :custom-report-schemas="availableCustomReportSchemas"
                    :show-description="index === 0"
                    :is-condensed-layout="true"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    CustomReportsTypeGuardService.isPayloadTemplateCustomReportSchema(
                      selectedCustomReport.schema
                    )
                  "
                >
                  <div class="mt-[20px]">
                    <JsonCustomReportConfiguration
                      v-if="activeCustomReportEditConfiguration != null"
                      :key="selectedCustomReport?.id"
                      :custom-report-schemas="availableCustomReportSchemas"
                      v-model="activeCustomReportEditConfiguration"
                      :editor-schema="
                        CustomReportJsonReportConfigurationService.getJsonEditorSchemaForCustomReport()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>

            <a-tabs
              v-else-if="selectedCustomReport != null"
              @change="onCustomReportViewModeTabChange"
              :key="selectedCustomReport?.id"
              v-model:activeKey="activeCustomReportItemViewMode.mode"
              :destroyInactiveTabPane="true"
            >
              <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                <CustomReportConfiguration
                  :configuration-level="1"
                  :custom-report="selectedCustomReport"
                  :custom-plots-output="customPlotsOutput"
                  :custom-tables-output="customTablesOutput"
                  @configuration-updated="handleConfigurationUpdate"
                  :custom-report-schemas="availableCustomReportSchemas"
                  :is-condensed-layout="true"
                />
              </a-tab-pane>
              <a-tab-pane
                key="edit"
                tab="JSON"
                :disabled="
                  CustomReportsTypeGuardService.isPayloadTemplateCustomReportSchema(
                    selectedCustomReport.schema
                  )
                "
              >
                <div class="mt-[20px]">
                  <JsonCustomReportConfiguration
                    v-if="activeCustomReportEditConfiguration != null"
                    :key="selectedCustomReport?.id"
                    :custom-report-schemas="availableCustomReportSchemas"
                    v-model="activeCustomReportEditConfiguration"
                    :editor-schema="
                      CustomReportJsonReportConfigurationService.getJsonEditorSchemaForCustomReport()
                    "
                    :place-holder="'Configure JSON'"
                    :configuration-level="1"
                    @update:modelValue="handleJsonEditorConfigurationChange"
                    :allow-type-change="false"
                  />
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <!-- Report Selection Modal -->
    <CustomReportSelectionModal
      v-model:visible="showCustomReportSelection"
      :custom-report-schemas="availableCustomReportSchemas"
      @custom-report-selected="addCustomReport"
    />

    <!-- Post Processsing Execution Selection Modal -->
    <Modal
      v-model:open="showPostProcessingSelectionModal"
      title="Select an Execution"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <ExecutionSelectionTable
          :execution-type-filter="[ExecutionPhaseType.Visualization]"
          @execution-selected="handlePostProcessingExecutionSelected"
        />
      </div>
    </Modal>

    <!-- JSON Export Drawer -->
    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Reports JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <CustomReportJsonManagement
        v-if="
          selectedPostProcessingExecutionId != null &&
          selectedCustomPlotsOutput != null &&
          availableCustomReportSchemas != null &&
          selectedPostProcessingExecutionId
        "
        :key="selectedCustomReport?.id"
        :parent-execution-id="selectedPostProcessingExecutionId"
        v-model:execution-name="executionName"
        :custom-report-schemas="availableCustomReportSchemas"
        :custom-reports="activeCustomReports"
        :visibility-changed="showJsonManagement"
        :custom-reports-json-parsing-service="customReportsJsonParsingService"
        @custom-reports-applied="onJsonManagementCustomReportsApplied"
      />
    </Drawer>
  </div>
</template>

<style scoped>
  /* make the button a flexible item and allow it to shrink in a flex row */
  .file-select-button {
    text-align: center;
    flex: 1 1 0; /* take remaining space but allow shrinking */
    min-width: 0; /* CRITICAL: allow shrinking so ellipsis can take effect */
  }

  /* Ensure the Ant button inner wrapper behaves as a constrained block */
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block; /* give the element a width */
    width: 100%; /* constrain to button width */
    overflow: hidden; /* required for ellipsis */
    white-space: nowrap; /* required for ellipsis */
    text-overflow: ellipsis; /* show "..." when truncated */
  }

  .reports-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Allow component to fill available space via flex but don't force parent to grow */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .reports-fill {
    flex: 1 1 0;
  }

  /* left list container becomes a flex column and will handle scrolling itself */
  .reports-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    /* show vertical scrollbar when content overflows */
    overflow-y: auto;
  }
  .fit-to-browser .reports-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }
  .reports-scroll-area {
    min-height: 0;
    /* let outer container handle scrolling to avoid nested scrollbars */
    overflow: visible;
  }
  .selected-report {
    border: 2px solid #1890ff;
  }
  .drop-zone-active {
    height: 4px;
    background-color: rgba(37, 99, 235, 0.2);
    transition: height 0.2s ease;
  }
  .report-moved-highlight {
    animation: reportMovedPulse 1.5s ease-in-out;
    border: 2px solid #10b981 !important;
    background-color: #ecfdf5 !important;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1) !important;
  }
  @keyframes reportMovedPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }
  .report-deleting {
    animation: reportDeletePulse 0.6s ease-in-out;
    border: 2px solid #ef4444 !important;
    background-color: #fef2f2 !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }
  @keyframes reportDeletePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    25% {
      transform: scale(1.02);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.3);
    }
    75% {
      transform: scale(0.98);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      opacity: 0;
    }
  }
  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Ensure splitter fills available vertical space and panels allow inner scrolling */
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  :deep(.p-splitter-panel) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
